import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Loader2, AlertTriangle, Star, MapPin, Calendar, Award,
  BookOpen, DollarSign, Globe, Phone, Mail, Download, Send,
  Building2, GraduationCap, TrendingUp, CheckCircle,
  ChevronRight, ExternalLink, Clock, Sparkles, X, Quote,
  Briefcase, Target, Shield, Zap, Users, Trophy, BarChart3,
  Home, FlaskConical, Dumbbell, Utensils, Wifi, Info
} from "lucide-react";
import { getCollegeById } from "../../services/api";
import { BrochureDownloadModal } from "./BrochureDownloadModal.js";

/* ─────────────────────────────────────────────────────────
   ANIMATION & OVERRIDE DESIGN SYSTEM CSS
───────────────────────────────────────────────────────── */
const ANIM_CSS = `
  @keyframes vz-fadeUp    { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
  @keyframes vz-fadeIn    { from{opacity:0} to{opacity:1} }
  @keyframes vz-scaleIn   { from{opacity:0;transform:scale(.95)} to{opacity:1;transform:scale(1)} }
  @keyframes vz-slideLeft { from{opacity:0;transform:translateX(-20px)} to{opacity:1;transform:translateX(0)} }
  @keyframes vz-spin      { to{transform:rotate(360deg)} }
  @keyframes vz-pulse     { 0%,100%{opacity:1} 50%{opacity:.5} }
  @keyframes vz-shimmer   { 0%{background-position:-400px 0} 100%{background-position:400px 0} }
  @keyframes vz-slideUp   { from{transform:translateY(40px);opacity:0} to{transform:translateY(0);opacity:1} }
  @keyframes vz-quoteSwap {
    0%{opacity:0;transform:translateY(14px)}
    12%{opacity:1;transform:translateY(0)}
    80%{opacity:1;transform:translateY(0)}
    100%{opacity:0;transform:translateY(-10px)}
  }

  .vz-fee-highlight {
    background: #f8faff;
    border-radius: 12px;
    padding: 16px 24px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05);
  }

  .vz-duration-chip {
    background: #f3f4f6;
    border-radius: 40px;
    padding: 6px 14px;
    color: #4b5563;
    font-size: 11px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    max-width: 100%;
    word-break: break-word;
  }

  .vz-category-tag {
    background: #eef2ff;
    color: #4338ca;
    font-weight: 700;
    font-size: 10px;
    letter-spacing: 0.05em;
    padding: 4px 12px;
    border-radius: 6px;
    display: inline-block;
  }
  .vz-fu  { animation: vz-fadeUp   .5s cubic-bezier(.16,1,.3,1) both }
  .vz-fi  { animation: vz-fadeIn   .4s ease both }
  .vz-si  { animation: vz-scaleIn  .45s cubic-bezier(.16,1,.3,1) both }
  .vz-sl  { animation: vz-slideLeft .45s cubic-bezier(.16,1,.3,1) both }
  .vz-d1  { animation-delay:.05s }
  .vz-d2  { animation-delay:.1s }
  .vz-d3  { animation-delay:.15s }
  .vz-d4  { animation-delay:.2s }

  .vz-hero-img { transition:transform 6s cubic-bezier(.16,1,.3,1) }
  .vz-hero:hover .vz-hero-img { transform:scale(1.03) !important }

  .vz-tab-active { position:relative }
  .vz-tab-active::after {
    content:''; position:absolute; bottom:-1px; left:0; right:0; height:3px;
    background:linear-gradient(90deg,#2563eb,#8b5cf6);
    border-radius:3px 3px 0 0;
  }

  .vz-card { transition:transform .2s ease, box-shadow .2s ease }
  .vz-card:hover { transform:translateY(-2px); box-shadow:0 12px 28px rgba(99,102,241,.12) }

  .vz-course { transition:all .2s ease; cursor: pointer; }
  .vz-course:hover { border-color:#2563eb; box-shadow:0 6px 20px rgba(37,99,235,.08); transform:translateX(3px) }

  .vz-fac { transition:all .2s ease }
  .vz-fac:hover { transform:translateY(-2px); box-shadow:0 6px 16px rgba(37,99,235,.1) }

  .vz-btn-apply   { transition:transform .2s ease, box-shadow .2s ease }
  .vz-btn-apply:hover   { transform:translateY(-2px); box-shadow:0 10px 24px rgba(249,115,22,.35) }
  .vz-btn-apply:active  { transform:translateY(0) }

  .vz-btn-brochure { transition:transform .2s ease, box-shadow .2s ease }
  .vz-btn-brochure:hover { transform:translateY(-2px); box-shadow:0 10px 24px rgba(37,99,235,.3) }
  .vz-btn-brochure:active { transform:translateY(0) }

  .vz-stat { transition:all .2s cubic-bezier(.16,1,.3,1); cursor: pointer; }
  .vz-stat:hover { transform:translateY(-4px); box-shadow:0 12px 28px rgba(37,99,235,.15); border-color:#2563eb; }

  .vz-chip { transition:all .15s ease }
  .vz-chip:hover { transform:scale(1.03); border-color:#2563eb }

  .vz-row { transition:all .2s ease }
  .vz-row:hover { transform:translateX(4px); background-color:rgba(249,250,251,0.8) }

  .vz-ci { transition:transform .2s ease }
  .vz-crow:hover .vz-ci { transform:scale(1.1) rotate(-3deg) }

  .vz-modal { animation:vz-slideUp .35s cubic-bezier(.16,1,.3,1) both }
  .vz-skel {
    background:linear-gradient(90deg,#f3f4f6 25%,#e5e7eb 50%,#f3f4f6 75%);
    background-size:400px 100%;
    animation:vz-shimmer 1.4s infinite linear;
  }
  .vz-logo { animation:vz-scaleIn .45s cubic-bezier(.16,1,.3,1) .15s both }
  .vz-qt { animation:vz-quoteSwap 4s ease-in-out both }
  .vz-noscroll::-webkit-scrollbar { display:none }
  .vz-noscroll { -ms-overflow-style:none; scrollbar-width:none }
`;

/* ─────────────────────────────────────────────────────────
   ANIMATED IMMERSIVE MODAL POPUP SYSTEM
───────────────────────────────────────────────────────── */
const ViewDetailsModal = ({ isOpen, onClose, title, content }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-md" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full max-h-[80vh] flex flex-col border border-slate-100 vz-modal" onClick={e => e.stopPropagation()}>
        <div className="p-4 sm:p-5 border-b border-slate-100 flex justify-between items-center bg-slate-50/80 backdrop-blur rounded-t-2xl">
          <h3 className="font-black text-slate-800 flex items-center gap-2 text-sm sm:text-base">
            <Sparkles className="w-4 h-4 text-blue-600" /> {title}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition p-1.5 rounded-xl hover:bg-slate-200/60"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 sm:p-6 overflow-y-auto text-xs sm:text-sm text-slate-600 font-medium leading-relaxed whitespace-pre-line break-words bg-white rounded-b-2xl max-h-[calc(80vh-100px)]">
          {content}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   TEXT SHORTENER WITH SAFE LINE-BREAK WRAPPING
───────────────────────────────────────────────────────── */
const TextTruncator = ({ text, title = "Details", limit = 160, isFee = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  if (!text) return null;
  
  const textStr = String(text).replace(/,([^\s])/g, ", $1");

  if (textStr.length <= limit) {
    return isFee ? (
      <span className="text-xl font-black text-blue-600 tracking-tight break-words">{textStr}</span>
    ) : (
      <p className="text-slate-600 leading-relaxed text-sm sm:text-base whitespace-pre-line break-words">{textStr}</p>
    );
  }

  return (
    <div className="min-w-0 w-full">
      {isFee ? (
        <span className="text-lg font-black text-blue-600 tracking-tight block truncate max-w-[200px]">
          {textStr.slice(0, 25)}...
        </span>
      ) : (
        <p className="text-slate-600 leading-relaxed text-xs sm:text-sm block break-words">
          {textStr.slice(0, limit)}...
        </p>
      )}
      <button onClick={() => setIsOpen(true)} className="inline-flex items-center gap-1 text-[11px] font-bold text-blue-600 tracking-wider mt-1 uppercase bg-transparent border-none cursor-pointer p-0">
        Click to View Full Details <Info className="w-3 h-3" />
      </button>
      <ViewDetailsModal isOpen={isOpen} onClose={() => setIsOpen(false)} title={title} content={textStr} />
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   ANIMATED QUOTE FALLBACK
───────────────────────────────────────────────────────── */
const QUOTES = [
  "Vidyazon: Your future, simplified.",
  "Where ambition meets opportunity.",
  "The right college is just a click away.",
  "Empowering the next generation of leaders.",
  "Guiding your journey from dream to degree.",
];

const AnimatedQuoteFallback = () => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => { setIdx(p => (p + 1) % QUOTES.length); }, 4000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="w-full h-full bg-gradient-to-r from-blue-700 via-purple-700 to-indigo-800 flex items-center justify-center p-6 text-center">
      <div className="max-w-md">
        <Quote className="w-8 h-8 text-white/20 mx-auto mb-3" />
        <p className="text-white text-base sm:text-lg font-medium italic px-4">{QUOTES[idx]}</p>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   UTILITIES
───────────────────────────────────────────────────────── */
const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  const host = (process.env.REACT_APP_BACKEND_URL || "http://localhost:5001/api").replace("/api", "");
  let c = path.startsWith("/") ? path.substring(1) : path;
  if (c.startsWith("uploads/")) c = c.substring(8);
  return `${host}/uploads/${c}`;
};

const fmtFee = (v) => {
  if (!v) return null;
  const n = Number(v);
  if (isNaN(n)) return v;
  return `₹${n.toLocaleString("en-IN")}`;
};

/* ─────────────────────────────────────────────────────────
   COLLEGE LOGO
───────────────────────────────────────────────────────── */
const CollegeLogo = ({ src, collegeName }) => {
  const [err, setErr] = useState(false);
  const [loading, setLoading] = useState(true);
  const url = getImageUrl(src);
  if (err || !url) return (
    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-md border-2 border-white flex-shrink-0">
      <span className="text-xl font-black text-white">{collegeName?.[0]?.toUpperCase() || "?"}</span>
    </div>
  );
  return (
    <div className="relative w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0">
      {loading && <div className="absolute inset-0 vz-skel rounded-xl border-2 border-white" />}
      <img src={url} alt={collegeName}
        className={`w-16 h-16 sm:w-20 sm:h-20 rounded-xl object-cover shadow-md border-2 border-white transition-opacity duration-300 ${loading ? "opacity-0" : "opacity-100"}`}
        onLoad={() => setLoading(false)} onError={() => { setErr(true); setLoading(false); }} />
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   APPLY MODAL
───────────────────────────────────────────────────────── */
const ApplyModal = ({ isOpen, onClose, collegeName }) => {
  const [form, setForm] = useState({ name: "", email: "", mobile: "", course: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  useEffect(() => { if (!isOpen) { setDone(false); setForm({ name: "", email: "", mobile: "", course: "" }); } }, [isOpen]);
  const handleSubmit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    await new Promise(r => setTimeout(r, 1200));
    setSubmitting(false); setDone(true);
  };
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[92vh] overflow-y-auto vz-modal" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-gray-100 flex justify-between items-start bg-gradient-to-r from-blue-600 to-indigo-600 rounded-t-2xl text-white">
          <div className="min-w-0 flex-1 pr-2">
            <h3 className="text-lg font-bold truncate">Apply Registration Hub</h3>
            <p className="text-xs text-blue-100 truncate mt-0.5">{collegeName}</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white transition p-1 rounded-lg hover:bg-white/20 flex-shrink-0"><X className="w-5 h-5" /></button>
        </div>
        {!done ? (
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            {[
              { key: "name",   label: "Full Name",       type: "text",  req: true,  ph: "Enter your legal name" },
              { key: "email",  label: "Email Address",   type: "email", req: true,  ph: "name@example.com" },
              { key: "mobile", label: "Mobile Number",   type: "tel",   req: true,  ph: "10-digit primary number" },
              { key: "course", label: "Preferred Course", type: "text",  req: false, ph: "e.g., B.Tech Computer Science" },
            ].map(({ key, label, type, req, ph }) => (
              <div key={key}>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1">
                  {label}{req && <span className="text-red-500 ml-0.5">*</span>}
                </label>
                <input type={type} required={req} value={form[key]}
                  onChange={e => setForm({ ...form, [key]: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none transition text-sm shadow-sm"
                  placeholder={ph} />
              </div>
            ))}
            <button type="submit" disabled={submitting}
              className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60 text-sm tracking-wide shadow-md vz-btn-apply mt-2">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" />Processing Profile...</> : <><Send className="w-4 h-4" />Submit Screening Request</>}
            </button>
          </form>
        ) : (
          <div className="p-6 text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-1">Application Logged!</h3>
            <p className="text-gray-500 text-xs sm:text-sm px-2">Academic screening algorithms matching parameter data fields. Counselors will dial your network track within operational parameters.</p>
            <button onClick={onClose} className="mt-5 w-full py-2 rounded-xl bg-gray-100 text-gray-700 hover:bg-gray-200 font-semibold transition text-xs uppercase tracking-wide">Dismiss</button>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   FACILITY ICON HELPER
───────────────────────────────────────────────────────── */
const facIcon = (name = "") => {
  const n = name.toLowerCase();
  if (n.includes("hostel"))  return <Home size={15} />;
  if (n.includes("librar"))  return <BookOpen size={15} />;
  if (n.includes("sport") || n.includes("gym")) return <Dumbbell size={15} />;
  if (n.includes("lab"))     return <FlaskConical size={15} />;
  if (n.includes("cafe") || n.includes("food")) return <Utensils size={15} />;
  if (n.includes("wifi") || n.includes("net"))  return <Wifi size={15} />;
  return <CheckCircle size={15} />;
};

/* ─────────────────────────────────────────────────────────
   MAIN LAYOUT STACK COMPONENT
───────────────────────────────────────────────────────── */
export default function CollegeDetail() {
  const { idOrName } = useParams();
  const navigate = useNavigate();
  const [college, setCollege] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("info");
  const [bannerError, setBannerError] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [showBrochureModal, setShowBrochureModal] = useState(false);
  const contactRef = useRef(null);

  const [activeOverlay, setActiveOverlay] = useState({ open: false, title: "", content: "" });

  useEffect(() => {
    const el = document.createElement("style");
    el.id = "vz-anim";
    el.textContent = ANIM_CSS;
    if (!document.getElementById("vz-anim")) document.head.appendChild(el);
    return () => { const s = document.getElementById("vz-anim"); if (s) s.remove(); };
  }, []);

  const fetchDetail = useCallback(async () => {
    setLoading(true); setError(""); setBannerError(false);
    try {
      const res = await getCollegeById(idOrName);
      const data = res.data?.data || res.data;
      if (data?.collegeName) setCollege(data);
      else { setCollege(null); setError(`College information matching index target "${idOrName}" could not be located inside database vectors.`); }
    } catch (err) {
      setError(err.response?.data?.message || "Internal microservice connection timeout exception.");
      setCollege(null);
    } finally { setLoading(false); }
  }, [idOrName]);

  useEffect(() => { if (idOrName) fetchDetail(); }, [idOrName, fetchDetail]);

  const isAuthenticated = () => !!(localStorage.getItem("adminToken") || localStorage.getItem("auth_token"));

  const triggerOverlay = (title, content) => {
    if(!content) return;
    const safeContent = String(content).replace(/,([^\s])/g, ", $1");
    setActiveOverlay({ open: true, title, content: safeContent });
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50/50 gap-3 px-4">
      <div className="relative w-12 h-12">
        <div className="absolute inset-0 rounded-full border-4 border-gray-200" />
        <div className="absolute inset-0 rounded-full border-4 border-t-blue-600 border-r-indigo-500 animate-spin" />
      </div>
      <p className="text-gray-400 text-xs font-bold uppercase tracking-widest animate-pulse">Syncing Matrix Core...</p>
    </div>
  );

  if (error || !college) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-6 sm:p-8 text-center border border-gray-100 vz-si">
        <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
        <h2 className="text-xl font-bold text-gray-800 mb-1">Index Missing Target</h2>
        <p className="text-gray-400 mb-5 text-xs sm:text-sm break-words">{error}</p>
        <Link to="/" className="inline-flex items-center justify-center gap-1.5 bg-gray-900 text-white text-xs font-bold uppercase tracking-wider px-5 py-2.5 rounded-xl transition hover:bg-gray-800">
          <ChevronRight className="w-4 h-4 rotate-180" /> Root Dashboard
        </Link>
      </div>
    </div>
  );

  const {
    collegeName = "", established = "", description = "", city = "", state = "", country = "",
    universityType = "", affiliatedBy = "", approvedBy = "",
    rating, totalReviews, bannerUrl, bannerImage, logoUrl, collegeLogo, brochureUrl,
    admissionProcess = "", eligibility = "", examAccepted = "", cutoffRanks = "",
    averagePackage = "", highestPackage = "", placementRate = "", topRecruiters = "",
    facilities: facilitiesRaw = "", accreditation = "", affiliations = "",
    highlights = "", phone = "", email = "", website = "", address = "", pincode = "",
    feeMin_INR, feeMax_INR,
    contact = {}, admissions = {}, placement = {}, rankings = [], scholarships = [],
    courses: coursesRaw = [],
    hostel, library, sports, labs, cafeteria,
    _id,
  } = college;

  const bannerSrc    = bannerUrl || bannerImage;
  const logoSrc      = logoUrl   || collegeLogo;
  const bannerImgUrl = getImageUrl(bannerSrc);

  const coursesList      = Array.isArray(coursesRaw) ? coursesRaw : [];
  const rankingsList     = Array.isArray(rankings)   ? rankings.filter(Boolean) : [];
  const scholarshipList  = Array.isArray(scholarships) ? scholarships.filter(Boolean) : [];
  
  let facilitiesList = [];
  if (hostel) facilitiesList.push("Hostel Residency");
  if (library) facilitiesList.push("Digital & Text Library");
  if (sports) facilitiesList.push("Sports Complexes");
  if (labs) facilitiesList.push("Advanced Scientific Labs");
  if (cafeteria) facilitiesList.push("Hygienic Cafeteria");

  if (Array.isArray(facilitiesRaw)) {
    facilitiesRaw.forEach(f => { if (f && !facilitiesList.includes(f)) facilitiesList.push(f); });
  } else if (typeof facilitiesRaw === "string" && facilitiesRaw) {
    facilitiesRaw.split(",").map(f => f.trim()).forEach(f => {
      if (f && !facilitiesList.includes(f)) facilitiesList.push(f);
    });
  }

  const cPhone   = contact?.phone   || phone;
  const cEmail   = contact?.email   || email;
  const cWebsite = contact?.website || website;

  const plHighest   = placement?.highest  || highestPackage;
  const plAverage   = placement?.average  || averagePackage;
  const plRate      = placement?.rate     || placementRate;
  const plCompanies = Array.isArray(placement?.companies) && placement.companies.length
    ? placement.companies
    : topRecruiters ? topRecruiters.split(",").map(r => r.trim()).filter(Boolean) : [];

  const admProcess  = admissions?.process      || admissionProcess;
  const admRequire  = admissions?.requirements || eligibility;
  const admExams    = admissions?.exams        || examAccepted;

  const highlightList  = highlights ? highlights.split(";").flatMap(h => h.split(".")).map(h => h.trim()).filter(Boolean) : [];

  const TABS = [
    { id: "info",        label: "Info",           icon: <BookOpen className="w-3.5 h-3.5" /> },
    { id: "courses",     label: "Courses & Fees", icon: <GraduationCap className="w-3.5 h-3.5" /> },
    { id: "admission",   label: "Admission",       icon: <CheckCircle className="w-3.5 h-3.5" /> },
    { id: "placement",   label: "Placement",       icon: <TrendingUp className="w-3.5 h-3.5" /> },
    { id: "ranking",     label: "Ranking",         icon: <Award className="w-3.5 h-3.5" /> },
    { id: "scholarship", label: "Scholarship",     icon: <DollarSign className="w-3.5 h-3.5" /> },
  ];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 antialiased overflow-x-hidden pt-16 sm:pt-20">
      <ApplyModal isOpen={showApplyModal} onClose={() => setShowApplyModal(false)} collegeName={collegeName} />
      <BrochureDownloadModal isOpen={showBrochureModal} onClose={() => setShowBrochureModal(false)}
        collegeId={_id} collegeName={collegeName} brochureUrl={brochureUrl}
        isLoggedIn={isAuthenticated()} userEmail={localStorage.getItem("user_email") || ""} />

      <ViewDetailsModal 
        isOpen={activeOverlay.open} 
        onClose={() => setActiveOverlay({ open: false, title: "", content: "" })} 
        title={activeOverlay.title} 
        content={activeOverlay.content} 
      />

      {/* ══════════════════════════════════════
          HERO
      ══════════════════════════════════════ */}
      <div className="vz-hero relative h-72 sm:h-80 lg:h-[380px] overflow-hidden bg-slate-950">
        {bannerImgUrl && !bannerError ? (
          <img src={bannerImgUrl} alt={`${collegeName} Banner`}
            className="vz-hero-img w-full h-full object-cover opacity-60 scale-105"
            onError={() => setBannerError(true)} />
        ) : <AnimatedQuoteFallback />}

        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent" />

        <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 lg:p-8 vz-fu">
          <div className="max-w-7xl mx-auto w-full">
            <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4 mb-4 mt-8 sm:mt-0 w-full">
              
              <div className="flex-1 min-w-0 flex items-start gap-3 sm:gap-4 w-full">
                <div className="vz-logo flex-shrink-0">
                  <CollegeLogo src={logoSrc} collegeName={collegeName} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap gap-1.5 mb-1.5">
                    {universityType && (
                      <span className="text-[9px] sm:text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-blue-600 text-white shadow-sm">
                        {universityType}
                      </span>
                    )}
                    {(accreditation || approvedBy) && (
                      <span className="text-[9px] sm:text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-purple-600 text-white shadow-sm">
                        {accreditation || approvedBy}
                      </span>
                    )}
                  </div>
                  
                  <h1 className="text-lg sm:text-2xl lg:text-4xl font-black text-white mb-2 drop-shadow break-words leading-tight">
                    {collegeName}
                  </h1>

                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-slate-300 text-xs">
                    {(city || state) && (
                      <span className="flex items-center gap-1 min-w-0 truncate">
                        <MapPin className="w-3.5 h-3.5 text-orange-400 flex-shrink-0" />
                        <span className="truncate">{[city, state, country].filter(Boolean).join(", ")}</span>
                      </span>
                    )}
                    {established && (
                      <span className="flex items-center gap-1 flex-shrink-0">
                        <Calendar className="w-3.5 h-3.5 text-orange-400 flex-shrink-0" /> Est. {established}
                      </span>
                    )}
                    {coursesList.length > 0 && (
                      <span className="flex items-center gap-1 flex-shrink-0">
                        <GraduationCap className="w-3.5 h-3.5 text-orange-400 flex-shrink-0" /> {coursesList.length} Tracks
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {rating && (
                <div className="flex-shrink-0 bg-white rounded-xl px-3.5 py-2 shadow-lg vz-si vz-d3 flex items-center gap-2.5 border border-slate-100 self-start sm:self-auto">
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400 flex-shrink-0" />
                  <span className="text-xl sm:text-2xl font-black text-slate-800 tracking-tight">{rating}</span>
                  <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider leading-none border-l border-slate-200 pl-2">
                    <div>{totalReviews || 0}</div>
                    <div className="mt-0.5">Reviews</div>
                  </div>
                </div>
              )}

            </div>

            <div className="flex flex-wrap gap-2 pt-3 border-t border-white/10 w-full overflow-hidden">
              {cutoffRanks && (
                <span className="flex items-center gap-1 text-[10px] sm:text-xs text-slate-300 bg-white/5 px-2.5 py-1 rounded-lg backdrop-blur-sm border border-white/5">
                  <Target className="w-3 h-3 text-blue-400" /> Cutoff: {cutoffRanks}
                </span>
              )}
              {rankingsList.length > 0 && rankingsList[0]?.rank && (
                <span className="flex items-center gap-1 text-[10px] sm:text-xs text-slate-300 bg-white/5 px-2.5 py-1 rounded-lg backdrop-blur-sm border border-white/5">
                  <Trophy className="w-3 h-3 text-yellow-400" /> Rank #{rankingsList[0].rank}
                </span>
              )}
              {feeMin_INR > 0 && (
                <span className="flex items-center gap-1 text-[10px] sm:text-xs text-slate-300 bg-white/5 px-2.5 py-1 rounded-lg backdrop-blur-sm border border-white/5">
                  <DollarSign className="w-3 h-3 text-green-400" /> From {fmtFee(feeMin_INR)}/yr
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════
          STICKY OPTIMIZED TABS
      ══════════════════════════════════════ */}
      <div className="sticky top-16 z-40 bg-white border-b border-slate-200 shadow-sm overflow-x-hidden">
        <div className="max-w-7xl mx-auto px-4 w-full">
          <div className="flex vz-noscroll overflow-x-auto w-full touch-pan-x">
            {TABS.map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)}
                className={`vz-tab-active flex items-center gap-1.5 px-4 py-3.5 font-bold whitespace-nowrap transition-all border-b-2 text-xs sm:text-sm flex-shrink-0
                  ${activeTab === t.id
                    ? "border-blue-600 text-blue-600 bg-gradient-to-t from-blue-50/20 to-transparent"
                    : "border-transparent text-slate-500 hover:text-slate-900 hover:bg-slate-50"}`}>
                {t.icon}{t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════
          CORE RESPONSIVE COLUMN GRID LAYOUT
      ══════════════════════════════════════ */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 w-full">
        <div className="flex flex-col lg:grid lg:grid-cols-3 gap-6 items-start w-full">

          {/* LEFT CONTENT BLOCK - EXPLICIT 2-COLUMN WIDTH GRIDS ON DESKTOP */}
          <div className="w-full lg:col-span-2 space-y-6 min-w-0">

            {/* TAB INDEX: GENERAL ACADEMIC OVERVIEW */}
            {activeTab === "info" && (
              <>
                <div 
                  className="bg-white rounded-2xl p-5 sm:p-6 shadow-sm border border-slate-200/60 vz-card vz-fu w-full cursor-pointer"
                  onClick={() => triggerOverlay(`About ${collegeName}`, description)}
                >
                  <h2 className="text-xl sm:text-2xl font-black text-slate-800 mb-4 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-blue-600" /> Executive Institutional Profile
                  </h2>
                  <TextTruncator 
                    text={description || `${collegeName} represents a benchmark center for advanced scholarship.`} 
                    title={`About ${collegeName}`} 
                    limit={320}
                  />
                  {highlightList.length > 0 && (
                    <div className="mt-5 space-y-2 w-full">
                      {highlightList.map((h, i) => (
                        <div key={i} className="flex items-start gap-2.5 p-3 bg-slate-50 rounded-xl border border-slate-100" style={{ animationDelay: `${i * 0.03}s` }}>
                          <Zap className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                          <p className="text-xs sm:text-sm text-slate-600 font-medium leading-relaxed break-words flex-1">{h}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="bg-gradient-to-br from-slate-900 to-indigo-950 rounded-2xl p-5 sm:p-6 text-white shadow-md border border-slate-800 vz-fu vz-d1 w-full">
                  <h3 className="text-base sm:text-lg font-bold mb-4 uppercase tracking-wider text-slate-300">Quick Fact Matrix</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 w-full">
                    {[
                      { show: affiliatedBy || affiliations, label: "Affiliated Control Board", val: affiliatedBy || affiliations, icon: <Globe className="w-4 h-4 text-blue-400" /> },
                      { show: approvedBy || accreditation, label: "Accreditation Threshold", val: approvedBy || accreditation, icon: <CheckCircle className="w-4 h-4 text-emerald-400" /> },
                      { show: coursesList.length > 0, label: "Curricular Segments", val: `${coursesList.length} Unique Specializations`, icon: <BookOpen className="w-4 h-4 text-purple-400" /> },
                      { show: established, label: "Track Longevity", val: `${new Date().getFullYear() - Number(established)}+ Operational Years`, icon: <Clock className="w-4 h-4 text-amber-400" /> }
                    ].map((item, index) => item.show ? (
                      <div key={index} className="flex items-start gap-3 bg-white/5 p-3 rounded-xl border border-white/5 min-w-0">
                        <div className="p-2 bg-white/5 rounded-lg flex-shrink-0 mt-0.5">{item.icon}</div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wide truncate">{item.label}</div>
                          <div className="font-semibold text-xs sm:text-sm text-slate-100 break-words mt-0.5 leading-snug">{item.val}</div>
                        </div>
                      </div>
                    ) : null)}
                  </div>
                </div>

                {facilitiesList.length > 0 && (
                  <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-sm border border-slate-200/60 vz-card vz-fu w-full">
                    <h3 className="text-lg sm:text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                      <Home className="w-4 h-4 text-indigo-600" /> Infrastructure Ecosystem
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 w-full">
                      {facilitiesList.map((f, i) => (
                        <div key={i} className="vz-fac flex items-center gap-2.5 p-3 rounded-xl bg-slate-50 border border-slate-200/50 text-slate-700 text-xs font-bold transition min-w-0 truncate">
                          <span className="text-blue-600 flex-shrink-0">{facIcon(f)}</span>
                          <span className="truncate flex-1">{f}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* TAB INDEX: CURRICULUM MANAGEMENT SYSTEMS */}
            {activeTab === "courses" && (
              <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-sm border border-slate-200/60 vz-fu space-y-5 w-full">
                <div className="border-b border-slate-100 pb-3">
                  <h2 className="text-xl sm:text-2xl font-black text-slate-800 flex items-center gap-2">
                    <GraduationCap className="w-5 h-5 text-blue-600" /> Catalogued Specializations
                  </h2>
                  <p className="text-slate-400 text-xs mt-1">Click anywhere on a program row to trigger an immersive full-screen syllabus and breakdown modal layout safely.</p>
                </div>

                {coursesList.length > 0 ? (
                  <div className="space-y-4 w-full">
                    {coursesList.map((course, idx) => {
                      const resolvedFee = course.fees || course.fee || course.courseFees || course.tuitionFeeTotal_INR || course.feeMax_INR || feeMin_INR;
                      const rawCourseName = String(course.courseName || course.name || "Program Path").replace(/,([^\s])/g, ", $1");
                      const rawDuration = String(course.courseDuration || course.duration || (course.durationMin_Months ? `${course.durationMin_Months} Months` : "N/A")).replace(/,([^\s])/g, ", $1");

                      const structureMeta = [
                        course.careers && `${course.careers} Careers`,
                        course.chapters && `${course.chapters} Chapters`,
                        course.videos && `${course.videos} Videos`,
                        course.pdfs && `${course.pdfs} PDFs`
                      ].filter(Boolean).join(" • ");

                      const fullRowDetails = `Program Specialization Name:\n${rawCourseName}\n\nTrack Duration & Metrics:\n${rawDuration} ${structureMeta ? `(${structureMeta})` : ""}\n\nAcademic Prerequisites:\n${course.eligibilityCriteria || "Baseline aggregate thresholds apply."}\n\nAllocated Fee Matrix Layout:\n${resolvedFee ? fmtFee(resolvedFee) : "Verification parameters pending direct desk updates."}`;

                      return (
                        <div 
                          key={course._id || idx} 
                          onClick={() => triggerOverlay(rawCourseName, fullRowDetails)}
                          className="vz-course border border-slate-200 rounded-xl p-4 sm:p-5 bg-white flex flex-col md:flex-row justify-between items-start md:items-center gap-4 w-full overflow-hidden"
                        >
                          <div className="space-y-2 min-w-0 flex-1 w-full">
                            <div className="flex flex-wrap items-center gap-2 w-full">
                              <div className="max-w-full flex-1 min-w-0">
                                <span className="text-base sm:text-lg font-black text-slate-800 block break-words leading-tight whitespace-normal">{rawCourseName}</span>
                              </div>
                              {course.courseCategory && <span className="vz-category-tag flex-shrink-0 self-start">{course.courseCategory}</span>}
                            </div>
                            
                            <div className="flex flex-wrap gap-1.5 items-center text-slate-500 w-full">
                              <span className="vz-duration-chip flex-shrink-0 max-w-full whitespace-normal">
                                <Clock size={11} className="flex-shrink-0 inline mr-1" /> 
                                <span className="break-words">{rawDuration}</span>
                              </span>
                              
                              {structureMeta && (
                                <span className="text-[10px] sm:text-xs text-slate-400 bg-slate-50 px-2.5 py-1 rounded-md font-medium border border-slate-100 break-words max-w-full">
                                  {structureMeta}
                                </span>
                              )}
                            </div>

                            {course.eligibilityCriteria && (
                              <div className="text-xs text-slate-500 bg-slate-50/50 p-2 rounded-lg border border-slate-100/80 max-w-full break-words">
                                <span className="font-bold text-slate-700">Constraint Index:</span> {course.eligibilityCriteria}
                              </div>
                            )}
                          </div>

                          <div className="text-left md:text-right flex-shrink-0 min-w-[140px] border-t border-dashed border-slate-100 pt-3 md:pt-0 md:border-none w-full md:w-auto">
                            <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Fee Structure</div>
                            <div className="text-xl font-black text-blue-600 tracking-tight break-words mt-0.5">
                              {resolvedFee ? (
                                String(resolvedFee).length > 22 ? `${String(resolvedFee).slice(0,20)}...` : fmtFee(resolvedFee)
                              ) : "N/A"}
                            </div>
                            <span className="text-[10px] text-blue-600 block font-bold uppercase tracking-wider mt-0.5">Click Card for Details →</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-10 bg-slate-50/50 rounded-xl border border-dashed border-slate-200">
                    <BookOpen className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-400 text-xs sm:text-sm">Dynamic specialized tracks pending digital ledger integration. Access advisement below.</p>
                  </div>
                )}
              </div>
            )}

            {/* TAB INDEX: ADMISSION PROTOCOLS */}
            {activeTab === "admission" && (
              <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-sm border border-slate-200/60 vz-fu space-y-6 w-full">
                <div className="border-b border-slate-100 pb-3">
                  <h2 className="text-xl sm:text-2xl font-black text-slate-800 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-emerald-600" /> Intake Protocols & Timelines
                  </h2>
                  <p className="text-slate-400 text-xs mt-1">Structured evaluation phases governing registration vectors for upcoming active cycles.</p>
                </div>

                <div className="relative border-l-2 border-blue-100 pl-5 sm:pl-6 ml-3 space-y-6">
                  
                  <div className="relative cursor-pointer" onClick={() => triggerOverlay("Gateway Entrance Requirements", admExams || "Standard profile indicators apply.")}>
                    <div className="absolute -left-[31px] sm:-left-[35px] top-0.5 bg-blue-600 text-white p-1 rounded-full shadow-md">
                      <Target className="w-3.5 h-3.5" />
                    </div>
                    <h3 className="text-sm font-bold uppercase tracking-wider text-blue-600">Phase 01: Gateway Evaluation</h3>
                    <div className="mt-1 bg-slate-50 rounded-xl p-3.5 border border-slate-100 text-xs sm:text-sm text-slate-600 leading-relaxed break-words hover:bg-slate-100/50 transition">
                      <span className="font-bold text-slate-800 block mb-1">Entrance Metrics Required:</span>
                      <p className="break-words whitespace-normal">{String(admExams || "Profile Assessment criteria apply directly.").replace(/,([^\s])/g, ", $1")}</p>
                      <span className="text-[10px] text-blue-600 font-bold block mt-1 uppercase">Click to expand framework overview</span>
                    </div>
                  </div>

                  <div className="relative cursor-pointer" onClick={() => triggerOverlay("Eligibility Aggregate Constraints", admRequire || "Standard baseline indices apply.")}>
                    <div className="absolute -left-[31px] sm:-left-[35px] top-0.5 bg-purple-600 text-white p-1 rounded-full shadow-md">
                      <Award className="w-3.5 h-3.5" />
                    </div>
                    <h3 className="text-sm font-bold uppercase tracking-wider text-purple-600">Phase 02: Index Prerequisite Verification</h3>
                    <div className="mt-1 bg-slate-50 rounded-xl p-3.5 border border-slate-100 text-xs sm:text-sm text-slate-600 leading-relaxed break-words hover:bg-slate-100/50 transition">
                      <span className="font-bold text-slate-800 block mb-1">Aggregate Threshold Index:</span>
                      <p className="break-words whitespace-normal">{String(admRequire || "Minimum baseline index matching credentials mandatory.").replace(/,([^\s])/g, ", $1")}</p>
                      <span className="text-[10px] text-purple-600 font-bold block mt-1 uppercase">Click to expand criteria filters</span>
                    </div>
                  </div>

                  <div className="relative cursor-pointer" onClick={() => triggerOverlay("Intake Methodology Parameters", admProcess || "Complete verification lifecycle maps apply.")}>
                    <div className="absolute -left-[31px] sm:-left-[35px] top-0.5 bg-slate-800 text-white p-1 rounded-full shadow-md">
                      <Building2 className="w-3.5 h-3.5" />
                    </div>
                    <h3 className="text-sm font-bold uppercase tracking-wider text-slate-800">Phase 03: Complete Step Methodology</h3>
                    <div className="mt-1 bg-slate-50 rounded-xl p-3.5 border border-slate-100 text-xs sm:text-sm text-slate-600 leading-relaxed break-words hover:bg-slate-100/50 transition">
                      <span className="font-bold text-slate-800 block mb-1.5">Mandatory Workflow Architecture:</span>
                      <p className="break-words whitespace-normal">{String(admProcess || "1. Record electronic validation. 2. Verify filters. 3. Match rankings. 4. Seat maps.").replace(/,([^\s])/g, ", $1")}</p>
                      <span className="text-[10px] text-slate-700 font-bold block mt-1 uppercase">Click to expand step tracker matrices</span>
                    </div>
                  </div>

                </div>
              </div>
            )}

            {/* TAB INDEX: PLACEMENT MATRIX */}
            {activeTab === "placement" && (
              <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-sm border border-slate-200/60 vz-fu space-y-6 w-full">
                <h2 className="text-xl sm:text-2xl font-black text-slate-800 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-orange-500" /> Corporate Placement Index</h2>
                
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 w-full">
                  <div 
                    onClick={() => triggerOverlay("Highest Placement Record Breakdown", plHighest || "Package tracking indices catalogued inside brochures.")}
                    className="vz-stat bg-gradient-to-br from-orange-50/60 to-orange-100/30 rounded-xl p-4 border border-orange-200/60 text-center min-w-0 flex flex-col justify-between"
                  >
                    <div className="text-lg sm:text-xl font-black text-orange-600 break-words max-w-full leading-tight whitespace-normal">{String(plHighest || "N/A").replace(/,([^\s])/g, ", $1")}</div>
                    <div>
                      <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wide mt-1">Highest Package</div>
                      <span className="text-[9px] text-orange-500 font-bold block mt-1 uppercase">Click for details</span>
                    </div>
                  </div>
                  
                  <div 
                    onClick={() => triggerOverlay("Average Placement Index Breakdown", plAverage || "Historical structural records managed via desk aggregates.")}
                    className="vz-stat bg-gradient-to-br from-blue-50/60 to-blue-100/30 rounded-xl p-4 border border-blue-200/60 text-center min-w-0 flex flex-col justify-between"
                  >
                    <div className="text-lg sm:text-xl font-black text-blue-600 break-words max-w-full leading-tight whitespace-normal">{String(plAverage || "N/A").replace(/,([^\s])/g, ", $1")}</div>
                    <div>
                      <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wide mt-1">Average Package</div>
                      <span className="text-[9px] text-blue-500 font-bold block mt-1 uppercase">Click for details</span>
                    </div>
                  </div>

                  <div 
                    onClick={() => triggerOverlay("Corporate Conversion Track Metrics", plRate || "Verified intake ratio statistics.")}
                    className="vz-stat bg-gradient-to-br from-emerald-50/60 to-emerald-100/30 rounded-xl p-4 border border-emerald-200/60 text-center min-w-0 flex flex-col justify-between"
                  >
                    <div className="text-lg sm:text-xl font-black text-emerald-600 break-words max-w-full leading-tight whitespace-normal">{String(plRate || "N/A").replace(/,([^\s])/g, ", $1")}</div>
                    <div>
                      <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wide mt-1">Conversion Index</div>
                      <span className="text-[9px] text-emerald-600 font-bold block mt-1 uppercase">Click for details</span>
                    </div>
                  </div>
                </div>

                {plCompanies.length > 0 && (
                  <div className="w-full">
                    <h3 className="font-bold text-slate-400 text-xs uppercase tracking-widest mb-3">Verified Institutional Recruiters</h3>
                    <div className="flex flex-wrap gap-2 w-full">
                      {plCompanies.map((c, i) => (
                        <span key={i} className="vz-chip px-3 py-1.5 bg-slate-50 border border-slate-200 text-slate-700 rounded-lg text-xs font-semibold select-none shadow-sm break-words">{c}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* TAB INDEX: RECOGNITION REGISTRIES */}
            {activeTab === "ranking" && (
              <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-sm border border-slate-200/60 vz-fu space-y-4 w-full">
                <h2 className="text-xl sm:text-2xl font-black text-slate-800 flex items-center gap-2"><Trophy className="w-5 h-5 text-yellow-500" /> Framework Recognitions</h2>
                {rankingsList.length > 0 ? (
                  <div className="divide-y divide-slate-100 w-full">
                    {rankingsList.map((r, i) => (
                      <div key={i} className="py-3 flex justify-between items-center gap-3 vz-row px-1 rounded-xl">
                        <div className="flex items-center gap-3 min-w-0">
                          <Award className="w-4 h-4 text-purple-600 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="font-bold text-slate-800 text-sm truncate">{r.agency || "National Survey"}</div>
                            <div className="text-[11px] text-slate-400 truncate mt-0.5">{r.category || "General Stream"}</div>
                          </div>
                        </div>
                        <div className="text-sm font-black text-purple-600 bg-purple-50 px-2.5 py-1 rounded-lg flex-shrink-0">#{r.rank}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-400 text-xs sm:text-sm">Verified algorithmic rank data indices are compiled within technical brochure datasets.</div>
                )}
              </div>
            )}

            {/* TAB INDEX: WAIVER SYLLABUS SCHEMES */}
            {activeTab === "scholarship" && (
              <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-sm border border-slate-200/60 vz-fu space-y-5 w-full">
                <div>
                  <h2 className="text-xl sm:text-2xl font-black text-slate-800 flex items-center gap-2"><DollarSign className="w-5 h-5 text-indigo-600" /> Merit Waiver Modules</h2>
                  <p className="text-slate-400 text-xs mt-1">Click on any scholarship tier down below to review extensive eligibility parameters in full view overlay profiles.</p>
                </div>

                {scholarshipList.length > 0 ? (
                  <div className="grid grid-cols-1 gap-3.5 w-full">
                    {scholarshipList.map((sch, idx) => {
                      const schTitle = sch.title || sch.name || "Merit Bracket Track";
                      const schContent = `Scholarship Title: ${schTitle}\n\nAllotment Amount / Waiver Segment:\n${sch.amount || "Variable Percentile Scaling"}\n\nDescription Parameters:\n${sch.description || "Assessed based on data templates."}\n\nPrerequisite Performance Threshold:\n${sch.criteria || "Verification rules configured on portal desks."}`;

                      return (
                        <div 
                          key={sch._id || idx} 
                          onClick={() => triggerOverlay(schTitle, schContent)}
                          className="border border-indigo-100 bg-indigo-50/20 rounded-xl p-4 sm:p-5 space-y-2 relative overflow-hidden w-full cursor-pointer hover:bg-indigo-50/50 hover:border-indigo-300 transition"
                        >
                          <div className="flex justify-between items-start gap-2 w-full">
                            <h4 className="font-bold text-indigo-950 text-base break-words flex-1 whitespace-normal">{String(schTitle).replace(/,([^\s])/g, ", $1")}</h4>
                            {sch.amount && <span className="bg-indigo-600 text-white text-[10px] font-black px-2.5 py-0.5 rounded-full flex-shrink-0 shadow-sm">{sch.amount}</span>}
                          </div>
                          <p className="text-slate-600 text-xs sm:text-sm leading-relaxed truncate">{sch.description || "Applicable based on evaluation percentiles checked during profiling."}</p>
                          <span className="text-[10px] text-indigo-600 font-bold block uppercase tracking-wide">Click card to review full prerequisite matrix →</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-10 bg-slate-50/50 rounded-xl border border-dashed border-slate-200">
                    <Users className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-400 text-xs sm:text-sm">No explicit localized waivers are configured online for this node. Inquire with desks below.</p>
                  </div>
                )}
              </div>
            )}

          </div>

          {/* ══════════════════════════════════════
              RIGHT SIDEBAR PANEL - 1/3 EXPLICIT LAYOUT
          ══════════════════════════════════════ */}
          <div className="w-full lg:col-span-1 lg:sticky lg:top-36 z-30">
            <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-sm border border-slate-200/70 space-y-4 w-full">
              <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest border-b border-slate-100 pb-2">Institutional Hub Portals</h3>
              
              <button onClick={() => setShowApplyModal(true)}
                className="w-full vz-btn-apply bg-gradient-to-r from-orange-500 to-red-500 text-white font-black py-3.5 rounded-xl shadow-sm flex items-center justify-center gap-2 text-xs uppercase tracking-wider">
                <Send className="w-4 h-4" /> Secure Screening Application
              </button>

              <button onClick={() => setShowBrochureModal(true)}
                className="w-full vz-btn-brochure bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-black py-3.5 rounded-xl shadow-sm flex items-center justify-center gap-2 text-xs uppercase tracking-wider">
                <Download className="w-4 h-4" /> Download Curriculum Catalogue
              </button>

              <div className="pt-4 border-t border-slate-100 space-y-3 w-full" ref={contactRef}>
                <h4 className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Admissions Desk Coordinates</h4>
                
                {cPhone && (
                  <a href={`tel:${cPhone}`} className="vz-crow flex items-center gap-3 text-slate-700 hover:text-blue-600 transition text-xs sm:text-sm font-semibold min-w-0">
                    <div className="vz-ci p-2 bg-blue-50 text-blue-600 rounded-lg flex-shrink-0"><Phone size={15} /></div>
                    <span className="truncate">{cPhone}</span>
                  </a>
                )}

                {cEmail && (
                  <a href={`mailto:${cEmail}`} className="vz-crow flex items-center gap-3 text-slate-700 hover:text-blue-600 transition text-xs sm:text-sm font-semibold min-w-0 break-all">
                    <div className="vz-ci p-2 bg-purple-50 text-purple-600 rounded-lg flex-shrink-0"><Mail size={15} /></div>
                    <span className="truncate">{cEmail}</span>
                  </a>
                )}

                {cWebsite && (
                  <a href={cWebsite} target="_blank" rel="noopener noreferrer" className="vz-crow flex items-center gap-3 text-slate-700 hover:text-blue-600 transition text-xs sm:text-sm font-semibold min-w-0">
                    <div className="vz-ci p-2 bg-emerald-50 text-emerald-600 rounded-lg flex-shrink-0"><Globe size={15} /></div>
                    <span className="truncate inline-flex items-center gap-1">Launch Verified Endpoint <ExternalLink size={11} /></span>
                  </a>
                )}

                {address && (
                  <div className="vz-crow flex items-start gap-3 text-slate-500 text-xs leading-relaxed pt-1 w-full">
                    <div className="vz-ci p-2 bg-amber-50 text-amber-600 rounded-lg flex-shrink-0 mt-0.5"><MapPin size={15} /></div>
                    <div className="min-w-0 flex-1 break-words">
                      {address}
                      {pincode && <span className="block font-bold mt-1 text-slate-700 text-[10px] uppercase tracking-wider">Digital Zone Index — {pincode}</span>}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}